<!-- Start the Group Training Organisation page wrapper. -->
<div class="gto-page">
  <!-- Start the hero section. -->
  <section class="hero">
    <!-- Add the dark overlay used to keep hero text readable over the image. -->
    <div class="hero-shade"></div>
    <!-- Keep the hero content aligned with the site's standard container width. -->
    <div class="container hero-inner">
      <!-- Display the page category above the main heading. -->
      <span class="pill">ECB Group Training</span>
      <!-- Display the main GTO page heading. -->
      <h1>Group Training Organisation <span>(GTO)</span></h1>
      <div class="ecb-web-voc-title-line"></div>
      <!-- Display the hero introduction explaining the purpose of ECB Group Training. -->
      <p>Linking employers with apprentices and trainees through supported, industry-focused employment pathways.</p>
      <!-- Keep the two key GTO actions immediately visible so users do not need to scroll to find them. -->
      <div class="hero-actions hero-primary-actions">
        <!-- Give apprentices and trainees a clear, immediate application action at the top of the page. -->
        <a routerLink="/contact-us" class="btn btn-orange hero-cta">
          <i class="bi bi-person-check-fill"></i>
          <span>Apply Now</span>
          <i class="bi bi-arrow-right"></i>
        </a>

        <!-- Give businesses an equally prominent host-employer action at the top of the page. -->
        <a routerLink="/contact-us" class="btn btn-host hero-cta">
          <i class="bi bi-building"></i>
          <span>Become a Host Employer</span>
          <i class="bi bi-arrow-right"></i>
        </a>
      </div>

      <!-- Keep the informational jump link visible, but secondary to the two main conversion actions. -->
      <a href="#how-it-works" class="hero-learn-link">
        <i class="bi bi-arrow-down-circle"></i>
        <span>See how the GTO model works</span>
      </a>
    </div>
  </section>

  <!-- Start the introductory explanation of what a GTO is. -->
  <section class="intro section-pad">
    <!-- Keep the introduction aligned with the site's standard container width. -->
    <div class="container grid-2">
      <!-- Display the introduction heading column. -->
      <div>
        <!-- Display the section label. -->
        <span class="section-kicker">What is a GTO?</span>
        <!-- Display the section heading with the highlighted phrase. -->
        <h2>A supported way to <span>earn, learn and grow</span></h2>
      </div>
      <!-- Display the detailed GTO explanation in a styled content card. -->
      <div class="copy-card">
        <!-- Explain ECB's role as the direct employer of apprentices and trainees. -->
        <p>A Group Training Organisation (GTO) is an approved employer of apprentices and trainees. As a GTO, ECB
          Training Services employs the apprentice or trainee directly — we sign the employment contract, pay the wages,
          and manage all employer obligations. We then place the apprentice or trainee with a host employer where they
          do their day-to-day work.</p>
        <!-- Explain the three-way relationship between ECB, the host employer and the worker. -->
        <p>This three-way relationship — ECB as employer, the host business as the workplace, and the apprentice or
          trainee as the worker — is what makes the GTO model unique in Australia. It means apprentices and trainees get
          job security and support, while host businesses get skilled workers without taking on the full burden of being
          a formal employer.</p>
      </div>
    </div>
  </section>

  <!-- Start the three-step GTO process section. -->
  <section id="how-it-works" class="process section-pad">
    <!-- Keep the process content aligned with the site's standard container width. -->
    <div class="container">
      <!-- Display the centered process section heading. -->
      <div class="section-head centered">
        <!-- Display the process section label. -->
        <span class="section-kicker">How the GTO Model Works</span>
        <!-- Display the process section title. -->
        <h2>Three simple steps</h2>
      </div>
      <!-- Create the responsive grid used for the three process cards. -->
      <div class="step-grid">
        <!-- Repeat one process card for every step defined in the component. -->
        <article *ngFor="let step of steps" class="step-card">
          <!-- Display the step number. -->
          <span class="step-number">{{ step.number }}</span>
          <!-- Display the step title. -->
          <h3>{{ step.title }}</h3>
          <!-- Display the step description. -->
          <p>{{ step.text }}</p>
        </article>
      </div>
    </div>
  </section>

  <!-- Start the reasons-to-choose-ECB section. -->
  <section class="why section-pad">
    <!-- Use a two-column layout for the workforce image and reasons list. -->
    <div class="container grid-2 align-center">
      <!-- Display the ECB workforce and training image. -->
      <div class="why-image">
        <!-- Load an existing project image rather than adding a new global asset dependency. -->
        <img src="assets/images/workforce-training.webp" alt="ECB training and workforce support">
      </div>
      <!-- Display the reasons-to-choose-ECB content. -->
      <div>
        <!-- Display the section label. -->
        <span class="section-kicker">Why ECB as Your GTO?</span>
        <!-- Display the section title. -->
        <h2>One organisation. <span>Complete support.</span></h2>
        <!-- Display the reasons in a custom checklist. -->
        <ul class="check-list">
          <!-- Repeat one checklist item for every reason defined in the component. -->
          <li *ngFor="let reason of reasons">
            <!-- Display the check icon. -->
            <i class="bi bi-check-circle-fill"></i>
            <!-- Display the reason text. -->
            <span>{{ reason }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>

  <!-- Start the apprentice, trainee and host-employer audience section. -->
  <section class="audiences section-pad">
    <!-- Keep the audience cards aligned with the site's standard container width. -->
    <div class="container">
      <!-- Display the apprentice and trainee information card. -->
      <div class="audience-card apprentice">
        <!-- Display the apprentice and trainee introductory content. -->
        <div>
          <!-- Display the audience label. -->
          <span class="section-kicker">For Apprentices and Trainees</span>
          <!-- Display the apprentice and trainee section title. -->
          <h2>Start your career with support behind you</h2>
          <!-- Explain how ECB employs and places apprentices and trainees. -->
          <p>ECB Training Services can employ you as an apprentice or trainee and place you with a host employer in your
            chosen industry.</p>
          <!-- Display the industries currently listed in the supplied requirements. -->
          <p class="industries"><strong>Industries:</strong> Construction · Resources and Infrastructure · Hospitality ·
            Community Services</p>
        </div>
        <!-- Display the apprentice and trainee benefits. -->
        <ul class="check-list compact">
          <!-- Repeat one list item for every apprentice benefit defined in the component. -->
          <li *ngFor="let item of apprenticeBenefits">
            <!-- Display the list check icon. -->
            <i class="bi bi-check2"></i>
            <!-- Display the apprentice benefit text. -->
            <span>{{ item }}</span>
          </li>
        </ul>
        <!-- Link prospective apprentices and trainees to the ECB contact page. -->
        <a routerLink="/contact-us" class="btn btn-orange">Apply Now</a>
      </div>

      <!-- Display the host-employer information card. -->
      <div class="audience-card employer">
        <!-- Display the host-employer introductory content. -->
        <div>
          <!-- Display the audience label. -->
          <span class="section-kicker">For Host Employers</span>
          <!-- Display the host-employer section title. -->
          <h2>Build capability without the full employer burden</h2>
          <!-- Explain how the host-employer relationship works. -->
          <p>Hosting an ECB apprentice or trainee gives your business access to motivated, supported workers — without
            taking on the full obligations of being an employer of record.</p>
        </div>
        <!-- Display the host-employer benefits. -->
        <ul class="check-list compact">
          <!-- Repeat one list item for every employer benefit defined in the component. -->
          <li *ngFor="let item of employerBenefits">
            <!-- Display the list check icon. -->
            <i class="bi bi-check2"></i>
            <!-- Display the host-employer benefit text. -->
            <span>{{ item }}</span>
          </li>
        </ul>
        <!-- Link prospective host employers to the ECB contact page. -->
        <a routerLink="/contact-us" class="btn btn-navy">Become a Host Employer</a>
      </div>
    </div>
  </section>
</div>
<!-- End the Group Training Organisation page wrapper. -->